import { useCallback, useEffect, useState } from 'react';
import { ArticleResponse, CategoryResponse, UpdateArticleRequest } from '../../../swagger/putujmoAdminService';
import { CrudEnum } from '../../../settings/routes';
import { Input } from '../../../components/Input/Input';
import { Button } from '../../../components/Button/Button';
import { MultiSelect } from '../../../components/MultiSelect/MultiSelect';
import { PutujmoSite, ArticleType } from '../../../swagger/putujmoAdminService';
import { useGoBackFromCrudAndPush } from '../../../utils/crudHooks';
import { LanguageEnum, languageOptions, siteOptions } from '../../../utils/languageUtils';
import { TextEditor } from '../../../components/Editor/TextEditor';
import { getAllCategoriesAction } from '../../../features/categories/action';
import { tryCatchJsonByAction } from '../../../utils/fetchUtils';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';

type Props = {
	values?: ArticleResponse
	onSubmit?: (newValues: UpdateArticleRequest) => Promise<void>
	onCancel?: () => void
	crudEnum: CrudEnum
}

export const AuthenticityForm = ({values, onSubmit, onCancel, crudEnum }: Props) => {
	const [putujmoSite, setPutujmoSite] = useState(values?.putujmoSite || PutujmoSite.Serbia);
	const [language, setLanguage] = useState(values?.language as LanguageEnum || LanguageEnum.Serbian);
	const [title, setTitle] = useState(values?.title || '');
	const [content, setContent] = useState(values?.content || '');
	const [imageUrl, setImageUrl] = useState(values?.imageUrl || '');
	const [location, setLocation] = useState(values?.location || '');
	const [phone, setPhone] = useState(values?.phone || '');
	const [email, setEmail] = useState(values?.email || '');
	const [website, setWebsite] = useState(values?.website || '');
	const [address, setAddress] = useState(values?.address || '');
	const [categoryIds, setCategoryIds] = useState(values?.categoryIds || []);

	const [loading, setLoading] = useState(false);
	const [categories, setCategories] = useState<CategoryResponse[]>([]);

	const submitCallback = useCallback(
		async (e: any) => {
			e.preventDefault();
			const model = new UpdateArticleRequest();
			model.putujmoSite = putujmoSite;
			model.title = title;
			model.content = content;
			model.language = language;
			model.imageUrl = imageUrl;
			model.location = location
			model.phone = phone
			model.email = email
			model.website = website
			model.address = address
			model.categoryIds = categoryIds;
			model.articleType = ArticleType.Authenticity;
			onSubmit && await onSubmit(model);
		},
		[
			putujmoSite,
			title,
			content,
			language,
			imageUrl,
			location,
			phone,
			email,
			website,
			address,
			categoryIds,
			onSubmit
		]
	)

	const fetchDataCallback = useCallback(
		async () => {
			setLoading(true);
			const bindedAction = getAllCategoriesAction.bind(null, putujmoSite, ArticleType.Authenticity, language)
			var getResponse = await tryCatchJsonByAction(bindedAction);
			if (getResponse.success) {
				const items = getResponse.response || [];
				setCategories(items);
			}
			setLoading(false);
		},
		[putujmoSite, language]
	)

	useEffect(
		() => {
			fetchDataCallback();
		},
		[fetchDataCallback]
	)

	const isRead = crudEnum === CrudEnum.Read;

	const goBackFromCrudAndPush = useGoBackFromCrudAndPush(CrudEnum.Read, undefined, undefined);

	const goUpdateCallback = useCallback(
		() => {
			if (values && values.id) {
				goBackFromCrudAndPush(`${CrudEnum.Update}/${values?.id}`);
			}
		},
		[values, goBackFromCrudAndPush]
	)

	if (loading) {
		return <LoadingSpinner />
	}

	return (
		<form onSubmit={submitCallback}>
			<div className='flex-between-container'>
				<MultiSelect
					label='Sajt'
					value={[putujmoSite]}
					items={siteOptions}
					onChange={(value) => {
						setPutujmoSite(value.length > 0 ? value[0] : PutujmoSite.Serbia)
					}}
					getItemId={(item: any) => item.id}
					getItemText={(item: any) => item.text}
					disabled={isRead}
				/>
				<MultiSelect
					label='Jezik'
					value={[language]}
					items={languageOptions}
					onChange={(value) => {
						setLanguage(value.length > 0 ? value[0] : LanguageEnum.Serbian)
					}}
					getItemId={(item: any) => item.id}
					getItemText={(item: any) => item.text}
					disabled={isRead}
				/>
				<Input
					label='Naziv'
					value={title}
					onChange={(value) => setTitle(value)}
					disabled={isRead}
				/>
				<Input
					label='Lokacija (Region)'
					value={location}
					onChange={(value) => setLocation(value)}
					disabled={isRead}
				/>
			</div>
			<div className='flex-between-container'>
				<Input
					label='Telefon'
					value={phone}
					onChange={(value) => setPhone(value)}
					disabled={isRead}
				/>
				<Input
					label='Email'
					value={email}
					onChange={(value) => setEmail(value)}
					disabled={isRead}
				/>
				<Input
					label='Adresa'
					value={address}
					onChange={(value) => setAddress(value)}
					disabled={isRead}
				/>
				<Input
					label='Web sajt'
					value={website}
					onChange={(value) => setWebsite(value)}
					disabled={isRead}
				/>
			</div>
			<div className='flex-between-container'>
				<div style={{width: '60%'}}>
					<Input
						label='Url naslovne slike'
						value={imageUrl}
						onChange={(value) => setImageUrl(value)}
						disabled={isRead}
					/>
				</div>
				<div style={{width: '30%'}}>
					<MultiSelect
						label='Kategorija'
						value={categoryIds}
						items={categories}
						onChange={(value) => {
							setCategoryIds(value)
						}}
						getItemId={(item: CategoryResponse) => item.id}
						getItemText={(item: CategoryResponse) => item.name}
						disabled={isRead}
						isClearable
						multiselect
					/>
				</div>
			</div>	
			<TextEditor
				// content rerender component
				value={values?.content || ''}
				onChange={(value) => setContent(value)}
				disabled={isRead}
			/>
			<div className='flex-end-container'>
				{!isRead && (
					<Button
						type='submit'
						value='Prihvati'
						disabled={
							!putujmoSite || 
							!title || 
							!language
						}
					/>
				)}
				{!isRead && (
					<Button
						type='button'
						value='Odustani'
						onClick={onCancel}
						outline
					/>
				)}
				{isRead && (
					<Button
						type='button'
						value='Izmeni'
						onClick={goUpdateCallback}
					/>
				)}
			</div>
		</form>
	)
}